import React, { useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { STATS, SOCIAL_LINKS } from '../data/constants';
import MatrixRain from './shared/MatrixRain';

const stagger = { visible: { transition: { staggerChildren: 0.08 } } };
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

const TITLES = [
  'Senior Software Engineer',
  'Technical Lead',
  'Startup Founder & Entrepreneur',
];

const TYPING_SPEED = 70;
const ERASING_SPEED = 40;
const PAUSE_AFTER_TYPE = 2000;
const PAUSE_AFTER_ERASE = 400;

const Hero = ({ onScrollTo }) => {
  const prefersReducedMotion = useReducedMotion();
  const [displayText, setDisplayText] = useState('');
  const [titleIndex, setTitleIndex] = useState(0);
  const [isErasing, setIsErasing] = useState(false);

  useEffect(() => {
    // Show first title statically when reduced motion is preferred
    if (prefersReducedMotion) {
      setDisplayText(TITLES[0]);
      return;
    }

    const currentTitle = TITLES[titleIndex];

    if (!isErasing) {
      if (displayText.length < currentTitle.length) {
        const timer = setTimeout(() => {
          setDisplayText(currentTitle.slice(0, displayText.length + 1));
        }, TYPING_SPEED);
        return () => clearTimeout(timer);
      }
      const timer = setTimeout(() => setIsErasing(true), PAUSE_AFTER_TYPE);
      return () => clearTimeout(timer);
    }
    if (displayText.length > 0) {
      const timer = setTimeout(() => {
        setDisplayText(displayText.slice(0, -1));
      }, ERASING_SPEED);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => {
      setIsErasing(false);
      setTitleIndex((i) => (i + 1) % TITLES.length);
    }, PAUSE_AFTER_ERASE);
    return () => clearTimeout(timer);
  }, [displayText, titleIndex, isErasing, prefersReducedMotion]);

  return (
    <section id="hero" className="relative pt-24 pb-20 md:pt-36 md:pb-28 overflow-hidden">
      {/* Matrix rain background */}
      <MatrixRain opacity={0.04} speed={0.8} density={0.5} />

      {/* Radial glow from top center */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(var(--color-green), 0.03) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-6xl mx-auto px-6 relative flex items-center gap-8 lg:gap-12">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl flex-1">
          {/* Status badge with avatar */}
          <motion.div variants={fadeUp} className="inline-flex items-center gap-3 px-3 py-1.5 mb-8 bg-raised border border-border rounded-full">
            <img
              src="avatar.webp"
              alt="Yehor Dremliuha"
              width="28"
              height="28"
              fetchPriority="high"
              className="w-7 h-7 rounded-full object-cover"
              onError={(e) => { e.target.style.visibility = 'hidden'; }}
            />
            <span className="w-2 h-2 rounded-full bg-green animate-pulse" />
            <span className="text-xs font-mono text-txt-secondary">available for opportunities</span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] mb-5 tracking-tight hover:text-glow transition-all duration-500"
          >
            Yehor Dremliuha
          </motion.h1>

          {/* Matrix-style typing title */}
          <motion.p
            variants={fadeUp}
            className="text-xl md:text-2xl text-green font-mono font-semibold mb-6 tracking-tight min-h-8 md:min-h-9"
          >
            {displayText}
            {!prefersReducedMotion && (
              <span className="inline-block w-[2px] h-[1.1em] bg-green ml-0.5 align-middle animate-blink" />
            )}
          </motion.p>

          <motion.p
            variants={fadeUp}
            className="text-base md:text-lg text-txt-secondary leading-relaxed mb-12 max-w-xl"
          >
            UK Global Talent Visa holder. Co-founded 4 companies, built systems processing
            1B+ events/year, and authored a 500-page engineering book with 1,100+ GitHub stars.
            Featured on the UK's #1 tech podcast.
          </motion.p>

          {/* Decorative gradient line */}
          <motion.div
            variants={fadeUp}
            className="w-full h-px mb-10 gradient-line-strong"
          />

          {/* Stats row */}
          <motion.div variants={fadeUp} className="flex flex-wrap gap-3 mb-10">
            {STATS.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 + i * 0.1, duration: 0.4 }}
                className="group px-4 py-2.5 bg-raised/80 border border-border rounded-lg hover:border-green/30 hover:border-glow transition-all duration-300"
              >
                <span className="text-lg font-mono font-bold text-green group-hover:text-green transition-colors">
                  {stat.value}
                </span>
                <span className="text-sm text-txt-secondary ml-2">{stat.label}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* CTAs */}
          <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
            <button
              onClick={() => onScrollTo('projects')}
              className="px-6 py-3 bg-green text-surface text-sm font-semibold rounded-lg btn-glow hover:bg-green/90 transition-all duration-300"
            >
              View My Work
            </button>
            <a
              href={`mailto:${SOCIAL_LINKS.email}`}
              className="px-6 py-3 border border-border text-txt text-sm font-medium rounded-lg hover:border-green/40 hover:text-green transition-all duration-300"
            >
              Get In Touch
            </a>
            <a
              href={SOCIAL_LINKS.github}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 border border-border text-txt-secondary text-sm font-medium rounded-lg hover:border-txt-tertiary hover:text-txt transition-all duration-300"
            >
              GitHub<span className="sr-only"> (opens in new tab)</span>
            </a>
          </motion.div>
        </motion.div>

        {/* Python logo on the right */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="hidden lg:flex shrink-0 items-center self-center"
        >
          <img
            src="snake_logo.webp"
            alt=""
            width="280"
            height="280"
            className="w-72 h-72 object-contain"
            style={{ filter: 'drop-shadow(0 0 40px rgba(var(--color-green), 0.12))' }}
            onError={(e) => { e.target.style.visibility = 'hidden'; }}
          />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
